import * as React from 'react';
import { useXyqarAuth } from '@xyqar/shared-auth';
import { apiGet, API_BASE } from '../lib/api';
import {
  PageTitle, Card, Pill, EmptyState, Kpi, Section,
  Table, Th, Td, Btn, fmt,
} from '../components/ui-kit';
import { Lock, ShieldCheck } from 'lucide-react';

interface Evidence {
  id: string;
  ts: string;
  kind: string;
  tenant?: string;
  sha256: string;
  zkp_proof?: boolean;
  size: number;
  download_path: string;
}

export default function EvidencePage() {
  const { getToken } = useXyqarAuth();
  const [items, setItems] = React.useState<Evidence[] | null>(null);
  const [filter, setFilter] = React.useState('');

  React.useEffect(() => {
    (async () => {
      try {
        const t = await getToken().catch(() => '');
        const j = await apiGet<{ evidence: Evidence[] }>('/api/v1/auditor/evidence', t);
        setItems(j.evidence ?? []);
      } catch { setItems([]); }
    })();
  }, [getToken]);

  const filtered = (items ?? []).filter((e) => !filter || e.kind.includes(filter) || e.id.includes(filter) || e.tenant?.includes(filter));
  const totalSize = (items ?? []).reduce((s, e) => s + e.size, 0);
  const zkp = (items ?? []).filter((e) => e.zkp_proof).length;
  const kinds = new Set((items ?? []).map((e) => e.kind)).size;

  return (
    <div>
      <PageTitle
        kicker="Audit · read-only"
        title="Sealed evidence library"
        right={<Pill tone="live"><ShieldCheck className="h-3 w-3" /> Ed25519 + SHA-256</Pill>}
      />

      <Section title="At a glance" hint="What can be inspected without production write access">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Kpi accent="blue"  label="Evidence items"     value={fmt(items?.length)} />
          <Kpi accent="green" label="With ZKP"           value={fmt(zkp)} />
          <Kpi              label="Distinct kinds"     value={fmt(kinds)} />
          <Kpi              label="Total size"         value={`${(totalSize / 1024 / 1024).toFixed(1)} MB`} />
        </div>
      </Section>

      <Section
        title="Evidence"
        hint="Each row is a tamper-evident artefact. Optional ZKP proof verifies a property without exposing identity data."
      >
        <input
          placeholder="Filter by kind / id / tenant…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full mb-6 rounded-md bg-white/5 border border-white/10 px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/40"
        />
        {items === null && <Card>Loading…</Card>}
        {items && items.length === 0 && <EmptyState title="No evidence available" hint="Nothing has been sealed for your current tenant scope yet." />}
        {items && filtered.length > 0 && (
          <Table>
            <thead>
              <tr><Th>Time</Th><Th>Kind</Th><Th>Tenant</Th><Th>SHA-256</Th><Th>ZKP</Th><Th className="text-right">Size</Th><Th></Th></tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.id} className="border-t border-white/5 hover:bg-white/5">
                  <Td><span className="text-[11px] text-white/40 font-mono">{new Date(e.ts).toLocaleString()}</span></Td>
                  <Td><span className="text-white text-xs font-mono">{e.kind}</span></Td>
                  <Td><span className="text-white/60 text-xs font-mono">{e.tenant ?? '—'}</span></Td>
                  <Td><span className="text-white/40 font-mono text-[10px]">{e.sha256.slice(0, 24)}…</span></Td>
                  <Td>{e.zkp_proof ? <Pill tone="live"><Lock className="h-2.5 w-2.5" /> ZKP</Pill> : <span className="text-white/30 text-xs">—</span>}</Td>
                  <Td className="text-right"><span className="text-white/60 text-xs font-mono">{(e.size / 1024).toFixed(1)} KB</span></Td>
                  <Td className="text-right"><Btn as="a" href={`${API_BASE}${e.download_path}`}>Download</Btn></Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>
    </div>
  );
}
